import {
  corpoRipeti,
  costoRun,
  descriviCosto,
  giornoOra,
  nomeEnvironment,
  nomeFlusso,
  nomeRun,
  passiRun,
  percentualeAvanzamento,
  quando,
  riassuntoRun,
  righeConCampagne,
  runAttiva
} from './Functions'
import { Icona } from './Icon'
import { t } from './testi'

/** La cella «Esito» di una run: stato se non è conclusa, altrimenti verdi/eseguiti e FAIL. */
const CellaEsito = ({ run, verdi, eseguiti, sottoSoglia }) => {
  if (run.stato === 'in_corso')
    return (
      <span className="nbadge nbadge--info">
        <Icona nome="pi-spin pi-spinner" /> {t.aeInCorso}
      </span>
    )
  if (run.stato === 'in_coda') return <span className="nbadge nbadge--info">{t.aeInCoda}</span>
  if (run.stato === 'fallita') return <span className="nbadge nbadge--err">{t.aeFallita}</span>
  return (
    <>
      <span>
        {verdi}/{eseguiti}
      </span>
      {run.stato === 'fermata' ? <span className="nbadge nbadge--warn">{t.aeFermata}</span> : null}
      {sottoSoglia ? (
        <span className="nbadge nbadge--err">{sottoSoglia} FAIL</span>
      ) : eseguiti ? (
        <span className="nbadge nbadge--ok">PASS</span>
      ) : null}
    </>
  )
}

/** Una riga dell'elenco; dentro una campagna è rientrata sotto la sua testata. */
const RigaRun = ({
  run,
  inCampagna,
  scelta,
  environments,
  inRipetizione,
  inEliminazione,
  onScegli,
  onRipeti,
  onElimina
}) => {
  const { verdi, eseguiti, saltati } = riassuntoRun(run)
  const passi = passiRun(run)
  const quandoRun = giornoOra(run.avviataIl)
  const avanzamento = passi.fatti + (passi.saltati ?? 0)
  const sottoSoglia = Math.max(0, eseguiti - verdi)
  const costo = descriviCosto(costoRun(run))
  const corpo = corpoRipeti(run)
  const classi = [
    scelta ? 'is-scelta' : '',
    inCampagna ? 'ae-riga-in-campagna' : '',
    run.stato === 'fallita' || (sottoSoglia && !runAttiva(run)) ? 'is-rossa' : ''
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <tr className={classi} onClick={() => onScegli(run.id)}>
      <td className="ae-quando">
        <div>{quandoRun.giorno}</div>
        <div className="ae-muto">{quandoRun.ora}</div>
      </td>
      <td>
        <button
          type="button"
          className="ae-nome ae-run-open"
          aria-haspopup="dialog"
          onClick={(evento) => {
            evento.stopPropagation()
            onScegli(run.id)
          }}
        >
          {nomeRun(run) || quando(run.avviataIl)}
        </button>
        <div className="ae-muto ae-run-meta">
          {nomeEnvironment(run.environmentId, environments)} · {nomeFlusso(run.contratto?.mode)}
        </div>
      </td>
      <td>{run.versioneHarness ? `v${run.versioneHarness}` : t.aeSenzaVersione}</td>
      <td className="ae-tabella__num">
        {avanzamento}/{passi.totali}
        {runAttiva(run) && passi.totali ? (
          <span
            className="ae-barra-passi"
            role="progressbar"
            aria-label={t.aeAvanzamento}
            aria-valuenow={percentualeAvanzamento(run)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span style={{ width: `${percentualeAvanzamento(run)}%` }} />
          </span>
        ) : null}
      </td>
      <td className="ae-tabella__esito">
        <CellaEsito run={run} verdi={verdi} eseguiti={eseguiti} sottoSoglia={sottoSoglia} />
      </td>
      <td className="ae-tabella__num is-ok">{passi.verdi}</td>
      <td className={`ae-tabella__num ${passi.rossi ? 'is-ko' : ''}`}>{passi.rossi ?? '—'}</td>
      <td className="ae-tabella__num ae-col-minore">{saltati}</td>
      <td className="ae-tabella__num ae-col-minore">{passi.errori ?? '—'}</td>
      <td className="ae-tabella__num ae-col-minore">{passi.nonValidi ?? '—'}</td>
      <td className="ae-tabella__num ae-costo-cella" title={costo}>
        {costo || '—'}
      </td>
      <td>
        <div className="ae-riga-azioni">
          {corpo ? (
            <button
              type="button"
              className="nbtn nbtn--ghost nbtn--sm nbtn--icon"
              aria-label={`${t.aeRipeti}: ${nomeRun(run) || quando(run.avviataIl)}`}
              title={t.aeRipeti}
              disabled={inRipetizione}
              onClick={(evento) => {
                evento.stopPropagation()
                onRipeti(run.id, corpo)
              }}
            >
              <Icona nome="pi-refresh" />
            </button>
          ) : null}
          <button
            type="button"
            className="nbtn nbtn--ghost nbtn--sm nbtn--icon"
            aria-label={`${t.aeElimina}: ${nomeRun(run) || quando(run.avviataIl)}`}
            title={t.aeElimina}
            disabled={inEliminazione}
            onClick={(evento) => {
              evento.stopPropagation()
              onElimina(run)
            }}
          >
            <Icona nome="pi-trash" />
          </button>
        </div>
      </td>
    </tr>
  )
}

/** La testata di una campagna: le sue run sotto, i numeri sommati qui. */
const RigaCampagna = ({ riga }) => (
  <tr className="ae-riga-campagna">
    <td className="ae-quando">
      <Icona nome="pi-sitemap" />
    </td>
    <td colSpan={3}>
      <strong>{riga.campagna.nome || t.aeCampagna}</strong>
      <div className="ae-muto ae-run-meta">
        {t.aeCampagna} · env {(riga.campagna.environmentIds ?? []).join(', ')} · {riga.runs.length}{' '}
        run
      </div>
    </td>
    <td className="ae-tabella__esito">
      <span>
        {riga.verdi}/{riga.eseguiti}
      </span>
      {riga.eseguiti - riga.verdi > 0 ? (
        <span className="nbadge nbadge--err">{riga.eseguiti - riga.verdi} FAIL</span>
      ) : null}
    </td>
    <td colSpan={2} />
    <td colSpan={3} className="ae-col-minore" />
    <td className="ae-tabella__num ae-costo-cella">{descriviCosto(riga.costo) || '—'}</td>
    <td />
  </tr>
)

/**
 * La tabella delle run, con le campagne raggruppate sotto la loro testata. Una riga si apre
 * con un clic o con il bottone sul nome (che è quello raggiungibile da tastiera).
 * @param {Object} props - Proprietà
 * @param {Array<Object>} props.runs - Le run della pagina
 * @param {string|null} props.runScelta - L'id della run aperta
 * @param {Array<Object>} props.environments - Gli environment
 * @param {string|null} props.ripetendo - L'id della run in ripetizione
 * @param {string|null} props.eliminando - L'id della run in eliminazione
 * @param {(id: string) => void} props.onScegli - Apre il dettaglio
 * @param {(id: string, corpo: Object) => void} props.onRipeti - Ripete una run
 * @param {(run: Object) => void} props.onElimina - Elimina una run (chiede conferma)
 */
export const TabellaRun = ({
  runs,
  runScelta,
  environments,
  ripetendo,
  eliminando,
  onScegli,
  onRipeti,
  onElimina
}) => {
  const riga = (run, inCampagna = false) => (
    <RigaRun
      key={run.id}
      run={run}
      inCampagna={inCampagna}
      scelta={run.id === runScelta}
      environments={environments}
      inRipetizione={ripetendo === run.id}
      inEliminazione={eliminando === run.id}
      onScegli={onScegli}
      onRipeti={onRipeti}
      onElimina={onElimina}
    />
  )
  return (
    <div className="ae-tabella-scroll">
      <table className="ae-tabella ae-tabella--runs">
        <caption className="ae-sr">{t.aeRun}</caption>
        <colgroup>
          <col className="ae-col-quando" />
          <col className="ae-col-nome" />
          <col className="ae-col-harness" />
          <col className="ae-col-avanzamento" />
          <col className="ae-col-esito" />
          <col className="ae-col-numero" span="2" />
          <col className="ae-col-numero ae-col-minore" span="3" />
          <col className="ae-col-costo" />
          <col className="ae-col-azione" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">{t.aeColQuando}</th>
            <th scope="col">{t.aeColNome}</th>
            <th scope="col">{t.aeHarness}</th>
            <th scope="col">{t.aeColPassi}</th>
            <th scope="col">{t.aeColEsito}</th>
            <th scope="col">{t.aeColVerdi}</th>
            <th scope="col">{t.aeColRossi}</th>
            <th scope="col" className="ae-col-minore">
              {t.aeScenariSaltati}
            </th>
            <th scope="col" className="ae-col-minore">
              {t.aeColErrori}
            </th>
            <th scope="col" className="ae-col-minore">
              {t.aeColNonValidi}
            </th>
            <th scope="col">{t.aeColCosto}</th>
            <th scope="col">
              <span className="ae-sr">{t.aeAzioni}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {righeConCampagne(runs).flatMap((r) =>
            r.tipo === 'run'
              ? [riga(r.run)]
              : [
                  <RigaCampagna key={`campagna-${r.campagna.id}`} riga={r} />,
                  ...r.runs.map((run) => riga(run, true))
                ]
          )}
        </tbody>
      </table>
    </div>
  )
}
